/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { CommonModule } from "@angular/common";
import { Component, OnInit, OnDestroy } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { Subject, takeUntil } from "rxjs";
import { ButtonModule } from "primeng/button";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { InputNumberModule } from "primeng/inputnumber";
import { InputTextModule } from "primeng/inputtext";
import { MessageService } from "primeng/api";
import { SkeletonModule } from "primeng/skeleton";

import {
  QPilotService,
  QPilotAccessToken,
} from "../../../../core/services/qpilot.service";
import { SessionService } from "../../../../core/services/session.service";
import { TimezoneHelper } from "../../../../core/utils/timezone.helper";
import { ProcessingCycleCompleteRequest } from "../../interfaces/processing-cycle.interface";
import { Site } from "../../../../core/models/site.interface";
import { ScheduledOrderListItem } from "../../interfaces/scheduled-orders-list-item.interface";

/**
 * Processing Cycle Complete Dialog
 * Allows completing a processing cycle manually
 * Matches old QMC CompleteCyclePopup functionality
 *
 * Supports dual mode:
 * - Standard mode: Uses SessionService for authentication
 * - Embedded mode: Uses inputToken passed via config.data
 */
@Component({
  selector: "app-processing-cycle-complete-dialog",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    InputNumberModule,
    InputTextModule,
    SkeletonModule,
  ],
  template: `
    <div class="complete-dialog">
      <!-- Loading State -->
      <div *ngIf="isLoadingNextOccurrence" class="loading-state">
        <p-skeleton width="100%" height="3rem" styleClass="mb-3" />
        <p-skeleton width="100%" height="3rem" styleClass="mb-3" />
        <p-skeleton width="100%" height="2rem" />
      </div>

      <!-- Form -->
      <div *ngIf="!isLoadingNextOccurrence" class="dialog-content">
        <!-- Charged Total -->
        <div class="field">
          <label for="chargedTotal" class="block mb-2 font-semibold">
            Charged Total <span class="text-red-500">*</span>
          </label>
          <p-inputNumber
            id="chargedTotal"
            [(ngModel)]="chargedTotal"
            mode="currency"
            currency="USD"
            locale="en-US"
            [minFractionDigits]="2"
            [maxFractionDigits]="2"
            styleClass="w-full"
            [required]="true"
          />
        </div>

        <!-- Payment Gateway Transaction ID -->
        <div class="field">
          <label for="transactionId" class="block mb-2 font-semibold">
            Payment Gateway Transaction ID <span class="text-red-500">*</span>
          </label>
          <input
            id="transactionId"
            type="text"
            pInputText
            [(ngModel)]="paymentGatewayTransactionId"
            class="w-full"
            placeholder="Enter transaction ID"
            required
          />
        </div>

        <!-- Learn More Link -->
        <div class="learn-more">
          <a
            href="https://docs.qpilot.cloud/docs/completing-a-processing-cycle"
            target="_blank"
            rel="noopener noreferrer"
            class="learn-more-link"
          >
            <i class="pi pi-external-link"></i>
            Learn more about completing a processing cycle
          </a>
        </div>

        <!-- Next Occurrence Info -->
        <div *ngIf="nextOccurrenceFromNow" class="next-occurrence-info">
          <i class="pi pi-info-circle"></i>
          <div class="info-content">
            <span class="info-label">Next Processing Date:</span>
            <span class="info-value">{{
              formatDate(nextOccurrenceFromNow)
            }}</span>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="dialog-footer">
        <button
          pButton
          label="Cancel"
          severity="secondary"
          [outlined]="true"
          (click)="onCancel()"
          [disabled]="isLoading"
        ></button>
        <button
          pButton
          label="Complete Cycle"
          (click)="onComplete()"
          [loading]="isLoading"
          [disabled]="!canComplete()"
        ></button>
      </div>
    </div>
  `,
  styles: [
    `
      .complete-dialog {
        display: flex;
        flex-direction: column;
        gap: 1.5rem;
      }

      .loading-state {
        padding: 1rem 0;
      }

      .dialog-content {
        display: flex;
        flex-direction: column;
        gap: 1.25rem;
      }

      .field {
        display: flex;
        flex-direction: column;
      }

      .next-occurrence-info {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        padding: 1rem;
        background-color: var(--blue-50);
        border-left: 4px solid var(--blue-500);
        border-radius: 6px;

        i {
          color: var(--blue-600);
          font-size: 1.2rem;
        }

        .info-content {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .info-label {
          font-size: 0.875rem;
          font-weight: 600;
          color: var(--blue-900);
        }

        .info-value {
          font-size: 1rem;
          color: var(--blue-900);
        }
      }

      .dialog-footer {
        display: flex;
        justify-content: flex-end;
        gap: 0.75rem;
        padding-top: 1rem;
        border-top: 1px solid var(--surface-border);
      }

      .text-red-500 {
        color: var(--red-500);
      }

      .learn-more {
        margin-top: 0.5rem;
      }

      .learn-more-link {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        color: var(--primary-color);
        text-decoration: none;
        font-size: 0.875rem;
        font-weight: 500;
        transition: color 0.2s ease;

        &:hover {
          color: var(--primary-700);
          text-decoration: underline;
        }

        i {
          font-size: 0.875rem;
        }
      }
    `,
  ],
})
export class ProcessingCycleCompleteDialogComponent
  implements OnInit, OnDestroy
{
  private readonly destroy$ = new Subject<void>();

  site: Site | null = null;
  scheduledOrder: ScheduledOrderListItem | null = null;
  chargedTotal: number = 0;
  paymentGatewayTransactionId: string = "";
  nextOccurrenceFromNow: Date | null = null;
  inputToken?: QPilotAccessToken;
  isLoading = false;
  isLoadingNextOccurrence = false;

  constructor(
    private qpilotService: QPilotService,
    private sessionService: SessionService,
    private timezoneHelper: TimezoneHelper,
    private config: DynamicDialogConfig,
    private ref: DynamicDialogRef,
    private messageService: MessageService,
  ) {}

  ngOnInit(): void {
    // Get data from config
    this.site = this.config.data?.site;
    this.scheduledOrder = this.config.data?.scheduledOrder;
    this.chargedTotal = this.config.data?.chargedTotal ?? 0;
    this.inputToken = this.config.data?.inputToken;

    this.loadNextOccurrence();
  }

  /**
   * Get access token based on mode
   */
  private getAccessToken(): QPilotAccessToken | undefined {
    if (this.inputToken) {
      return this.inputToken;
    }
    return this.sessionService.accessToken ?? undefined;
  }

  /**
   * Load next occurrence date
   */
  private loadNextOccurrence(): void {
    const accessToken = this.getAccessToken();
    if (!this.scheduledOrder || !this.site || !accessToken) {
      return;
    }

    this.isLoadingNextOccurrence = true;

    this.qpilotService
      .getScheduledOrderNextOccurrence(
        this.scheduledOrder,
        this.site.Id,
        accessToken,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (nextOccurrence) => {
          this.nextOccurrenceFromNow = new Date(nextOccurrence);
          this.isLoadingNextOccurrence = false;
        },
        error: (error) => {
          console.error("Failed to load next occurrence:", error);
          this.isLoadingNextOccurrence = false;
        },
      });
  }

  /**
   * Check if can complete - both Charged Total and Transaction ID are required
   */
  canComplete(): boolean {
    return (
      this.chargedTotal > 0 &&
      this.paymentGatewayTransactionId.trim().length > 0
    );
  }

  /**
   * Format date with timezone
   */
  formatDate(date: Date | null): string {
    if (!date) return "-";
    return this.timezoneHelper.formatDateWithTimezone(
      date,
      this.site?.UtcOffset ?? 0,
    );
  }

  /**
   * Cancel dialog
   */
  onCancel(): void {
    this.ref.close();
  }

  /**
   * Complete cycle
   */
  onComplete(): void {
    const accessToken = this.getAccessToken();

    if (!this.canComplete() || !this.scheduledOrder || !this.site) {
      return;
    }

    if (!accessToken) {
      this.showError("Session expired. Please refresh the page.");
      return;
    }

    const request: ProcessingCycleCompleteRequest = {
      ChargedTotal: this.chargedTotal,
      PaymentGatewayTransactionId: this.paymentGatewayTransactionId,
    };

    this.isLoading = true;

    this.qpilotService
      .completeProcessingCycle(
        this.site.Id,
        this.scheduledOrder.Id,
        request,
        accessToken,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isLoading = false;
          this.ref.close(request);
        },
        error: (error) => {
          console.error("Failed to complete cycle:", error);
          this.isLoading = false;
          this.showError("Failed to complete processing cycle");
        },
      });
  }

  /**
   * Show error message
   */
  private showError(message: string): void {
    this.messageService.add({
      severity: "error",
      summary: "Error",
      detail: message,
      life: 3000,
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
